/**
 * Extract start parameter from Telegram initData
 */
export const extractStartParam = (initData: string | undefined): string | null => {
  if (!initData) return null;

  const urlParams = new URLSearchParams(initData);
  return urlParams.get('start_param');
};

/**
 * Get primary param (first segment before --)
 * e.g. "ref_123--yclid_456" → "ref_123"
 */
const getPrimaryParam = (startParam: string | null): string | null => {
  if (!startParam) return null;
  const idx = startParam.indexOf('--');
  return idx >= 0 ? startParam.substring(0, idx) : startParam;
};

/**
 * Extract a segment by prefix from any position in the combined start param
 * e.g. extractSegment("ref_123--yclid_456", "yclid_") → "456"
 */
const extractSegment = (startParam: string | null, prefix: string): string | null => {
  if (!startParam) return null;
  const segments = startParam.split('--');
  for (const seg of segments) {
    if (seg.startsWith(prefix)) {
      return seg.substring(prefix.length);
    }
  }
  return null;
};

/**
 * Extract referral telegram ID from start param
 * @param startParam - e.g. "ref_123456789" or "ref_123--yclid_456"
 * @returns telegram ID or null
 */
export const extractReferralId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('ref_')) {
    return null;
  }

  return primary.substring(4); // Remove 'ref_' prefix
};

/**
 * Extract case opening ID from start param
 * @param startParam - e.g. "co_uuid-here"
 * @returns case opening ID or null
 */
export const extractCaseOpeningId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('co_')) {
    return null;
  }

  return primary.substring(3); // Remove 'co_' prefix
};

/**
 * Extract inventory item ID from start param
 * @param startParam - e.g. "item_uuid-here"
 * @returns inventory item ID or null
 */
export const extractInventoryItemId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('item_')) {
    return null;
  }

  return primary.substring(5); // Remove 'item_' prefix
};

/**
 * Extract giveaway ID from start param
 * @param startParam - e.g. "giveaway_uuid-here"
 * @returns giveaway ID or null
 */
export const extractGiveawayId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('giveaway_')) {
    return null;
  }

  return primary.substring(9); // Remove 'giveaway_' prefix
};

/**
 * Extract promocode from start param
 * @param startParam - e.g. "promocode" or "promocode_SHOT10"
 * @returns promocode value or null if not a promocode param
 */
export const extractPromocodeParam = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary) return null;

  if (primary === 'promocode') {
    return ''; // Just open promocode screen without prefilling
  }

  if (primary.startsWith('promocode_')) {
    return primary.substring(10); // Remove 'promocode_' prefix
  }

  return null;
};

/**
 * Check if start param indicates profile navigation
 * @param startParam - e.g. "profile"
 * @returns true if should navigate to profile
 */
export const isProfileParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'profile';
};

/**
 * Check if start param indicates inventory navigation
 * @param startParam - e.g. "inventory"
 * @returns true if should navigate to inventory
 */
export const isInventoryParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'inventory';
};

/**
 * Check if start param indicates wheel navigation
 * @param startParam - e.g. "wheel"
 * @returns true if should navigate to wheel
 */
export const isWheelParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'wheel';
};

export const isReferralsParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'referrals';
};

/**
 * Check if start param indicates the achievements catalog.
 * Opened through the profile tab so the back stack is [profile, achievements].
 * @param startParam - e.g. "achievements"
 */
export const isAchievementsParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'achievements';
};

/**
 * Check if start param opens the Rush Mid product survey directly.
 * Used by broadcast deep links: t.me/<bot>/<app>?startapp=rushmidsurvey.
 * Opened through the games tab so back returns to games, not an empty stack.
 * @param startParam - e.g. "rushmidsurvey"
 */
export const isRushMidSurveyParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'rushmidsurvey';
};

/**
 * Extract a RushMid party invite code from the start param.
 * @param startParam - e.g. "party_ABCD" → "ABCD" (the RushMid deep link auto-joins on open)
 */
export const extractPartyCode = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('party_') ? p.slice('party_'.length) : null;
};

/**
 * Extract a RushMid open-game id from the start param.
 * @param startParam - e.g. "game_<matchId>" → "<matchId>" (opens the game's joiner slot grid)
 */
export const extractGameId = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('game_') ? p.slice('game_'.length) : null;
};

/**
 * Extract a RushMid match id from a shared replay start param.
 * @param startParam - e.g. "replay_<matchId>" → "<matchId>" (opens the replay viewer)
 */
export const extractReplayId = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('replay_') ? p.slice('replay_'.length) : null;
};

/**
 * Check if start param opens the RushMid games list directly (the «Игры» home tab).
 * @param startParam - e.g. "rushmid" → lands on GamesHomeScreen (no party/game auto-join)
 */
export const isRushMidParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'rushmid';
};

/**
 * Check if start param opens the Roulette rooms list. Roulette is deep-link-only —
 * no games-tab tile — so this param is the sole TMA entry point.
 * @param startParam - e.g. "roulette" → lands on the Roulette rooms screen
 */
export const isRouletteParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'roulette';
};

/**
 * Extract user ID from start param for public profile viewing
 * @param startParam - e.g. "profile_uuid-here"
 * @returns user ID or null
 */
export const extractUserId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('profile_')) {
    return null;
  }

  return primary.substring(8); // Remove 'profile_' prefix
};

/**
 * Extract yclid (Yandex Click ID) from any segment
 */
export const extractYclid = (startParam: string | null): string | null =>
  extractSegment(startParam, 'yclid_');

/**
 * Extract campaign ID from any segment
 */
export const extractCampaignId = (startParam: string | null): string | null =>
  extractSegment(startParam, 'cid_');

/**
 * Extract ad ID from any segment
 */
export const extractAdId = (startParam: string | null): string | null =>
  extractSegment(startParam, 'aid_');

/**
 * Возвращает URL для открытия бота в Telegram
 * @returns URL бота в формате https://t.me/cs_shot_bot/app
 */
export function getBotUrl(): string {
  const botUsername = 'cs_shot_bot';
  return `https://t.me/${botUsername}/app`;
}
